// Authenticator enrolment: scan the QR, confirm with a live code, then save the
// recovery codes. Used both at first sign-in (where it is compulsory) and from
// the Security page when moving to a new device.

import { useState, type FormEvent } from 'react';
import { type EnrolmentOffer } from '../lib/api';
import { fieldErrors, useErrorMessage } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { Icon } from './Icon';
import { ErrorAlert, TextField } from './ui';

export function TwoFactorSetup({ offer, onConfirm, busyLabel }: {
  offer: EnrolmentOffer;
  /** Resolves with the recovery codes once the code is accepted. */
  onConfirm: (code: string) => Promise<void>;
  busyLabel?: string;
}) {
  const t = useT();
  const toMessage = useErrorMessage();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onConfirm(code.trim());
    } catch (err) {
      setError(fieldErrors(err).code ?? toMessage(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="stack">
      <p className="muted">{t('twofactor.setUpIntro')}</p>

      <div className="totp-setup">
        <img className="totp-setup__qr" src={offer.qr} alt="" width={240} height={240} />
        <div className="stack stack--sm">
          <div className="field">
            <span className="field__label">{t('twofactor.manualEntry')}</span>
            {/* Grouped in fours so it can be typed or read aloud accurately. */}
            <code className="totp-setup__secret">{offer.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
          </div>
        </div>
      </div>

      {error && <ErrorAlert message={error} />}

      <TextField
        label={t('twofactor.code')}
        hint={t('twofactor.codeHint')}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder={t('twofactor.codePlaceholder')}
        maxLength={7}
        required
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />

      <button type="submit" className="btn btn--primary btn--block" disabled={busy || code.trim().length < 6}>
        {busy ? <span className="spinner" /> : <Icon name="shield" />}
        {busy ? (busyLabel ?? t('twofactor.verifying')) : t('twofactor.confirm')}
      </button>
    </form>
  );
}

/** The one-time display of recovery codes. Deliberately hard to skip past. */
export function RecoveryCodes({ codes, onAcknowledge }: {
  codes: string[];
  onAcknowledge: () => void;
}) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const asText = codes.join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the codes are on screen regardless.
    }
  };

  const download = () => {
    const blob = new Blob([`ARES Invoice — recovery codes\n\n${asText}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ares-invoice-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="stack">
      <div className="alert alert--warning">
        <Icon name="alert-circle" />
        <div>{t('twofactor.recoveryIntro')}</div>
      </div>

      <ul className="recovery-codes">
        {codes.map((code) => <li key={code}><code>{code}</code></li>)}
      </ul>

      <div className="row">
        <button type="button" className="btn btn--sm" onClick={download}>
          <Icon name="download" size={14} />{t('twofactor.recoveryDownload')}
        </button>
        <button type="button" className="btn btn--sm" onClick={() => void copy()}>
          <Icon name={copied ? 'check' : 'file-text'} size={14} />
          {copied ? t('twofactor.recoveryCopied') : t('twofactor.recoveryCopy')}
        </button>
      </div>

      <button type="button" className="btn btn--primary btn--block" onClick={onAcknowledge}>
        {t('twofactor.recoveryAcknowledge')}
      </button>
    </div>
  );
}
